import React from 'react';
import ufovSymbolSets from '../lib/ufovSymbolSets';
import { UFOVSymbol, useUFOVSymbolVariant } from './UFOVSymbol';

const { getUFOVSymbolDisplayName } = ufovSymbolSets;

const DEFAULT_PERIPHERAL_POINT = { x: 78, y: 31 };

const getOnboardingScenario = (symbolVariant) => symbolVariant?.scenario?.onboarding || {};

const getBoardScenario = (symbolVariant) => symbolVariant?.scenario?.board || {};

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const mapPointToSafeZone = (point, safeZone) => {
    const x = clampPercent(point?.x ?? 50);
    const y = clampPercent(point?.y ?? 50);

    return {
        x: safeZone.left + ((safeZone.right - safeZone.left) * (x / 100)),
        y: safeZone.top + ((safeZone.bottom - safeZone.top) * (y / 100)),
    };
};

export const createUFOVExampleTrial = (symbolVariant) => {
    const example = symbolVariant.onboardingExample || {};
    const onboardingScenario = getOnboardingScenario(symbolVariant);
    const noiseLayout = onboardingScenario.noiseLayout || [];
    const desiredPeripheralPoint = example.peripheralPoint || DEFAULT_PERIPHERAL_POINT;
    const replacementSlotIndex = onboardingScenario.reservePeripheralNoiseSlot && noiseLayout.length
        ? noiseLayout.reduce((bestIndex, item, index) => {
            const bestItem = noiseLayout[bestIndex];
            const bestDistance = Math.hypot(
                bestItem.x - desiredPeripheralPoint.x,
                bestItem.y - desiredPeripheralPoint.y
            );
            const nextDistance = Math.hypot(
                item.x - desiredPeripheralPoint.x,
                item.y - desiredPeripheralPoint.y
            );

            return nextDistance < bestDistance ? index : bestIndex;
        }, 0)
        : -1;
    const peripheralPoint = replacementSlotIndex >= 0
        ? {
            x: noiseLayout[replacementSlotIndex].x,
            y: noiseLayout[replacementSlotIndex].y,
        }
        : desiredPeripheralPoint;

    return {
        centerSymbol: example.centerAnswer,
        peripheralSymbol: example.peripheralSymbolId,
        peripheralPoint,
        distractors: noiseLayout
            .filter((_, index) => index !== replacementSlotIndex)
            .map((item) => ({
                ...item,
                symbol: example.noiseSymbolId,
            })),
    };
};

export function UFOVBoardSurface({
    trial,
    centerSymbol,
    showNoise = true,
    showCenter = true,
    showPeripheral = true,
    muted = false,
    masked = false,
    demo = false,
}) {
    const symbolVariant = useUFOVSymbolVariant();
    const boardScenario = getBoardScenario(symbolVariant);
    const boardTrial = trial || createUFOVExampleTrial(symbolVariant);
    const centerValue = centerSymbol || boardTrial.centerSymbol;
    const hasBoardArt = Boolean(symbolVariant.board?.path);
    const boardRef = React.useRef(null);
    const [boardScale, setBoardScale] = React.useState(1);
    const getSymbolMotionStyle = (motion, rotation = 0) => ({
        '--UFOV-symbol-rotation': `${rotation}deg`,
        '--UFOV-symbol-move-x': `${motion?.moveX || 0}px`,
        '--UFOV-symbol-move-y': `${motion?.moveY || 0}px`,
        '--UFOV-symbol-spin': `${motion?.spin || 0}deg`,
        '--UFOV-symbol-duration': `${motion?.durationMs || 2400}ms`,
        '--UFOV-symbol-delay': `${motion?.delayMs || 0}ms`,
    });
    const boardStyle = {
        ...(symbolVariant.board?.path ? { '--UFOV-board-image': `url("${symbolVariant.board.path}")` } : {}),
        '--UFOV-board-scale': boardScale.toFixed(4),
    };
    const toBoardPoint = (point) => mapPointToSafeZone(
        point,
        boardScenario.safeZone || {
            left: 0,
            right: 100,
            top: 0,
            bottom: 100,
        }
    );
    const peripheralPoint = toBoardPoint(boardTrial.peripheralPoint || DEFAULT_PERIPHERAL_POINT);

    React.useEffect(() => {
        const board = boardRef.current;

        if (!board) {
            return undefined;
        }

        let animationFrame = 0;
        const syncScale = () => {
            window.cancelAnimationFrame(animationFrame);
            animationFrame = window.requestAnimationFrame(() => {
                const width = board.getBoundingClientRect().width;
                const nextScale = Number.isFinite(width) && width > 390
                    ? width / 390
                    : 1;

                setBoardScale((currentScale) => (
                    Math.abs(currentScale - nextScale) > 0.005
                        ? nextScale
                        : currentScale
                ));
            });
        };

        syncScale();

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(syncScale);
            observer.observe(board);

            return () => {
                window.cancelAnimationFrame(animationFrame);
                observer.disconnect();
            };
        }

        window.addEventListener('resize', syncScale);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', syncScale);
        };
    }, []);

    return (
        <div
            className={`UFOV-game__board ${demo ? 'is-demo' : ''} ${muted ? 'is-muted' : ''} ${hasBoardArt ? 'has-board-art' : ''}`.trim()}
            ref={boardRef}
            style={boardStyle}
            aria-hidden="true"
        >
            <div className="UFOV-game__board-grid" />

            {boardTrial.distractors?.map((item) => {
                const point = toBoardPoint(item);
                const noiseFontSize = Math.round((item.size || 22) * (boardScenario.noiseScale || 1));

                return (
                    <span
                        key={item.id}
                        className={`UFOV-game__board-noise ${showNoise ? '' : 'is-hidden'}`.trim()}
                        style={{
                            left: `${point.x}%`,
                            top: `${point.y}%`,
                            fontSize: `calc(${noiseFontSize}px * var(--UFOV-board-scale, 1))`,
                            opacity: showNoise
                                ? Math.min(
                                    boardScenario.noiseOpacityMax ?? 1,
                                    (item.opacity ?? 0.56) + (boardScenario.noiseOpacityBoost || 0)
                                )
                                : 0,
                            ...getSymbolMotionStyle(item.motion, item.rotation || 0),
                        }}
                    >
                        <UFOVSymbol symbol={item.symbol} usage="board" />
                    </span>
                );
            })}

            <span
                className={`UFOV-game__board-peripheral ${showPeripheral ? '' : 'is-hidden'}`.trim()}
                style={{
                    left: `${peripheralPoint.x}%`,
                    top: `${peripheralPoint.y}%`,
                    ...getSymbolMotionStyle(boardTrial.peripheralMotion),
                }}
            >
                <UFOVSymbol symbol={boardTrial.peripheralSymbol} usage="board" />
            </span>

            <div className="UFOV-game__board-center">
                <span className={showCenter ? '' : 'is-hidden'}>
                    <UFOVSymbol symbol={centerValue} usage="board" />
                </span>
            </div>

            {masked ? (
                <div className="UFOV-game__board-mask" />
            ) : null}
        </div>
    );
}

export function UFOVChoiceGrid({ options, selected, onSelect }) {
    const symbolVariant = useUFOVSymbolVariant();

    return (
        <div className="UFOV-game__choice-grid">
            {options.map((symbolId, index) => (
                <button
                    key={symbolId}
                    aria-label={`${getUFOVSymbolDisplayName(symbolVariant.id, symbolId)} option ${index + 1}`}
                    className={`UFOV-game__choice-tile ${selected === symbolId ? 'is-selected' : ''}`}
                    type="button"
                    onClick={() => onSelect(symbolId)}
                >
                    <UFOVSymbol className="UFOV-game__choice-symbol" symbol={symbolId} usage="choice" />
                </button>
            ))}
        </div>
    );
}

export function UFOVDirectionPad({ positions, selected, onSelect }) {
    return (
        <div className="UFOV-game__direction-pad">
            {positions.map((position) => (
                <button
                    key={position.id}
                    className={`UFOV-game__direction-tile ${selected === position.id ? 'is-selected' : ''}`}
                    style={{ gridArea: position.gridArea }}
                    type="button"
                    onClick={() => onSelect(position.id)}
                >
                    {position.label}
                </button>
            ))}
            <div className="UFOV-game__direction-center" style={{ gridArea: 'c' }} />
        </div>
    );
}
